import { useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { PictureHotspot } from '../contentTypes'

/** Меньше этого по любой стороне — промах мимо картинки, а не область */
const MIN_SIDE = 0.02

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

/**
 * Картинка задания с выделением областей мышью или пальцем.
 *
 * Координаты области задаются рамкой прямо на изображении: набирать доли от
 * левого верхнего угла руками — работа, которую учитель делать не должен, и
 * промахнуться в ней проще, чем попасть.
 *
 * Хранится центр области и её размер в долях от картинки, поэтому разметка
 * переживает и смену размеров превью, и другой экран у ученика.
 */
export function HotspotCanvas({
  imageUrl,
  hotspots,
  onChange,
  disabled = false,
}: {
  imageUrl: string
  hotspots: PictureHotspot[]
  onChange: (hotspots: PictureHotspot[]) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const boxRef = useRef<HTMLDivElement | null>(null)

  // Рамка в процессе рисования: пока тянут — это не область, а намерение
  const [draft, setDraft] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null)

  const pointFromEvent = (event: React.PointerEvent): { x: number; y: number } | null => {
    const box = boxRef.current?.getBoundingClientRect()
    if (!box || box.width === 0 || box.height === 0) return null

    return {
      x: clamp01((event.clientX - box.left) / box.width),
      y: clamp01((event.clientY - box.top) / box.height),
    }
  }

  const handlePointerDown = (event: React.PointerEvent) => {
    if (disabled) return
    const point = pointFromEvent(event)
    if (!point) return

    // Захват указателя: без него рамка теряется, стоит увести курсор с картинки
    event.currentTarget.setPointerCapture(event.pointerId)
    setDraft({ x1: point.x, y1: point.y, x2: point.x, y2: point.y })
  }

  const handlePointerMove = (event: React.PointerEvent) => {
    if (disabled || draft === null) return
    const point = pointFromEvent(event)
    if (!point) return

    setDraft({ ...draft, x2: point.x, y2: point.y })
  }

  const handlePointerUp = () => {
    if (draft === null) return

    const w = Math.abs(draft.x2 - draft.x1)
    const h = Math.abs(draft.y2 - draft.y1)
    setDraft(null)

    if (w < MIN_SIDE || h < MIN_SIDE) return

    onChange([
      ...hotspots,
      {
        x: (draft.x1 + draft.x2) / 2,
        y: (draft.y1 + draft.y2) / 2,
        w,
        h,
        answers: [''],
      },
    ])
  }

  const draftRect = draft && {
    left: `${Math.min(draft.x1, draft.x2) * 100}%`,
    top: `${Math.min(draft.y1, draft.y2) * 100}%`,
    width: `${Math.abs(draft.x2 - draft.x1) * 100}%`,
    height: `${Math.abs(draft.y2 - draft.y1) * 100}%`,
  }

  return (
    <Box>
      <Box
        ref={boxRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setDraft(null)}
        sx={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          // Иначе жест рисования уводит страницу в прокрутку на тачскрине
          touchAction: 'none',
          cursor: disabled ? 'default' : 'crosshair',
          userSelect: 'none',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt=""
          draggable={false}
          // display: block и height: auto — картинка держит пропорции. В колонке
          // flex она растягивалась по ширине, а maxHeight резал высоту отдельно,
          // и превью выходило сплющенным
          sx={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: 1 }}
        />

        {hotspots.map((hotspot, index) => {
          const w = hotspot.w ?? 0
          const h = hotspot.h ?? 0
          const isPoint = w <= 0 || h <= 0

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                left: `${(hotspot.x - w / 2) * 100}%`,
                top: `${(hotspot.y - h / 2) * 100}%`,
                ...(isPoint
                  ? { width: 28, height: 28, transform: 'translate(-50%, -50%)', borderRadius: '50%' }
                  : { width: `${w * 100}%`, height: `${h * 100}%`, borderRadius: 1 }),
                border: 2,
                borderColor: 'primary.main',
                bgcolor: 'rgba(103, 58, 183, 0.18)',
                display: 'flex',
                alignItems: isPoint ? 'center' : 'flex-start',
                justifyContent: isPoint ? 'center' : 'flex-start',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  px: 0.5,
                  borderRadius: '10px',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: 12,
                  lineHeight: '20px',
                  textAlign: 'center',
                }}
              >
                {index + 1}
              </Box>
            </Box>
          )
        })}

        {draftRect && (
          <Box
            sx={{
              position: 'absolute',
              ...draftRect,
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'primary.main',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {t('exercises.editor.hotspotDrawHint')}
      </Typography>
    </Box>
  )
}
